<script>
import FriendsMenuIcon from "@/assets/svgs/f-menu.vue";
import FriendsAddIcon from "@/assets/svgs/f-add.vue";
import { debounce } from "lodash";

const MIN_SEARCH_LENGTH = 4;

export default {
  name: "FriendsList",

  props: {
    showAddFriends: {
      type: Boolean,
      default: false,
    },
  },

  components: { FriendsAddIcon, FriendsMenuIcon },

  data: () => ({
    activeContainer: "friendList",
    panel: 0,
    tab: "sent",
    addFriendsTab: "search username",
    friendRequestCategoriesTitles: ["sent", "received", "rejected"],
    friendsListCategoriesTitles: ["search username", "last played with"],
    selectedUser: "",
    search: "",
    lastsearch: "",
    isFocused: false,
    loading: false
  }),

  computed: {
    isEnoughCharacters() {
      return this.search?.length >= MIN_SEARCH_LENGTH;
    },
    lastPlayedWith() {
      return this.$store.getters.getLastPlayedWith || [];
    },
    displayedItems() {
      if (!this.search || this.search?.length < 4) {
        return [];
      }

      if (this.selectedUser && this.selectedUser === this.search) {
        return [this.selectedUser];
      }

      return this.$store.getters.getPossibleFriends || [];
    },
    friendRequestCategories() {
      return {
        sent: this.userFriendsSent,
        received: this.userFriendsReceived,
        rejected: this.userFriendsRejected,
      };
    },

    userFriendsBlocked() {
      let friends = [];

      if (this.$store.getters.getBlockedFriendsList) {
        friends = this.$store.getters.getBlockedFriendsList;
      }

      return friends;
    },

    userFriendsRejected() {
      let friends = [];

      if (this.$store.getters.getRejectedFriendsList) {
        friends = this.$store.getters.getRejectedFriendsList;
      }

      return friends;
    },

    userFriendsSent() {
      let friends = [];

      if (this.$store.getters.getSentFriendsList) {
        friends = this.$store.getters.getSentFriendsList;
      }

      return friends;
    },

    userFriendsReceived() {
      let friends = [];

      if (this.$store.getters.getReceivedFriendsList) {
        friends = this.$store.getters.getReceivedFriendsList;
      }

      return friends;
    },

    friendList() {
      return this.$store.state.userFriendsAccepted || [];
    },

    userFriendsOnline() {
      return this.friendList.filter(friend => friend.online);
    },

    userFriendsOffline() {
      return this.friendList.filter(friend => !friend.online);
    },
  },

  watch: {
    search: {
      handler() {
        this.loading = true;
        this.updateSearch();
      },
      immediate: true,
    },
    selectedUser: {
      handler() {
        this.loading = true;
        this.updateSearch();
      },
      immediate: true,
    },
  },

  methods: {
    updateSearch: debounce(function () {
      if (this.selectedUser && this.selectedUser !== this.lastsearch) {
        this.lastsearch = this.selectedUser;
        this.search = this.selectedUser;
      }

      if (this.search !== this.lastsearch) {
        const blockedFrom = this.$store.getters.getUserBlockedFromList || [];
        const friends = this.friendList;
        const blockedAndFriendsDisplayNames = [...blockedFrom, ...friends].map((a) => a.displayName);
        const usersInProcess = [
          ...this.userFriendsSent.map((a) => a.displayName),
          ...this.userFriendsReceived.map((a) => a.displayName),
        ];
        const currentUserDN = this.$store.getters.getuser.displayName;
        const searchString = this.search?.trim() || "";
        const excludeUsers = [
          ...blockedAndFriendsDisplayNames.filter((a) =>
            a.toLowerCase().includes(searchString),
          ),
          ...usersInProcess.filter((a) =>
            a.toLowerCase().includes(searchString),
          ),
          currentUserDN,
        ];

        this.selectedUser = "";
        this.lastsearch = this.search;
        this.$store.dispatch("searchDisplayName", {
          searchString: searchString?.replaceAll(" ", ""),
          excludeUsers: excludeUsers,
        });
      }

      this.loading = false;
    }, 300),
    async addFriend(displayName) {
      if (!displayName) {
        return;
      }

      const response = await this.$store.dispatch(
        "sendfriendrequest",
        displayName,
      );

      this.selectedUser = "";

      const text =
        response.status === "ERROR"
          ? response.message
          : "Your friend request was sent successfully!";

      await this.$store.dispatch("showNotificationTooltip", text);
    },
    async acceptFriendRequest(item) {
      const response = await this.$store.dispatch(
        "acceptfriendrequest",
        item.friendId,
      );

      if (response.status === "SUCCESS") {
        await this.$store.dispatch("removeFriendRequestNotification", item);
        await this.$store.dispatch("showNotificationTooltip", "Friend Request accepted");
      } else {
        await this.$store.dispatch("showNotificationTooltip", "Couldn't Accept Friend Request");
      }
    },
    async rejectFriendRequest(item) {
      const response = await this.$store.dispatch(
        "rejectfriendrequest",
        item.friendId,
      );

      if (response.status === "SUCCESS") {
        await this.$store.dispatch("removeFriendRequestNotification", item);
        await this.$store.dispatch("showNotificationTooltip", "Friend Request rejected");
      } else {
        await this.$store.dispatch("showNotificationTooltip", "Couldn't reject friend request");
      }
    },
    async unblockUserConfirm(item) {
      const response = await this.$store.dispatch("unblockuser", item.friendId);
      const text =
        response.status === "SUCCESS"
          ? "User unblocked successfully"
          : "Couldn't unblock the user";

      await this.$store.dispatch("showNotificationTooltip", text);
    },
    async blockItemConfirm(user) {
      const response = await this.$store.dispatch("blockuser", user.friendId);
      const text =
        response.status === "SUCCESS"
          ? "User blocked successfully"
          : "Couldn't block the user";

      await this.$store.dispatch("showNotificationTooltip", text);
      await this.$store.dispatch("hideConfirmDialog");
    },
    async removeFriendConfirm(user) {
      const response = await this.$store.dispatch(
        "removefriend",
        user.friendId,
      );
      const text =
        response.status === "SUCCESS"
          ? "Friend removed"
          : "Couldn't remove friend";

      await this.$store.dispatch("showNotificationTooltip", text);
      await this.$store.dispatch("hideConfirmDialog");
    },

    setActiveContainer(item) {
      this.activeContainer = item;
    },
    setTab(item) {
      this.tab = item;
    },
    setFriendsTab(item) {
      this.addFriendsTab = item;
    },

    removeFriend(item) {
      this.$store.dispatch("showConfirmDialog", {
        title: "Removing friend",
        text: `Are you sure you want to remove ${item.displayName} from friends?`,
        callback: () => {
          this.removeFriendConfirm(item);
        },
      });
    },
    blockUser(item) {
      this.$store.dispatch("showConfirmDialog", {
        title: "Blocking user",
        text: `Are you sure you want to block ${item.displayName}?`,
        callback: () => {
          this.blockItemConfirm(item);
        },
      });
    },
    unblockUser(item) {
      this.$store.dispatch("showConfirmDialog", {
        title: "Unblocking user",
        text: `Are you sure you want to unblock ${item.displayName}?`,
        callback: () => {
          this.unblockUserConfirm(item);
        },
      });
    },
    close() {
      this.dialogAddFriend = false;
    },
  },
};
</script>

<template>
  <v-row
    class="h-full py-6 py-md-4 py-lg-12 d-flex justify-center friends-list-container"
  >
    <v-col
      v-if="showAddFriends"
      :class="{
        active: activeContainer === 'addFriends',
      }"
      class="friend-container left d-flex justify-center px-0 pt-0"
      cols="4"
      @mouseover="setActiveContainer('addFriends')"
    >
      <v-card
        class="card-background-light ma-0 pa-0"
        elevation="10"
        height="100%"
        max-width="400"
        min-height="400"
        width="100%"
      >
        <v-card-title class="d-fle align-center justify-space-between">
          <friends-add-icon width="50px" />

          <span class="title--text"> ADD FRIENDS </span>
        </v-card-title>

        <v-divider class="mt-1" />
        <v-divider class="mb-1" />

        <v-card-text class="pl-0 pr-0">
          <div class="d-flex px-3">
            <v-btn
              v-for="key in friendsListCategoriesTitles"
              :key="key"
              :class="{
                'main-lighter': addFriendsTab === key,
              }"
              :ripple="false"
              class="friend-request-button font-weight-superbold mx-1"
              elevation="0"
              height="60px"
              style="flex: 1"
              @click="setFriendsTab(key)"
            >
              <span
                class="text-subtitle-1 text-md-h6 bold position-relative friend-add-text py-3"
              >
                {{ key }}
              </span>
            </v-btn>
          </div>

          <div
            v-if="addFriendsTab === 'search username'"
            class="px-4 mt-4"
          >
            <v-autocomplete
              v-model="selectedUser"
              :hint="isFocused ? 'Please type at least 4 characters' : ''"
              :items="displayedItems"
              :loading="loading"
              :menu-props="{
                dark: true,
                offsetY: true,
                contentClass: `menu-container ${
                  isEnoughCharacters ? '' : 'd-none'
                }`,
              }"
              :no-filter="true"
              :search-input.sync="search"
              class="black-input hinted"
              dark
              placeholder="Search..."
              prepend-inner-icon="mdi-magnify"
              solo
              @blur="isFocused = false"
              @focus="isFocused = true"
            >
              <template #append>
                <v-btn
                  v-if="selectedUser"
                  class="add-friend-button"
                  icon
                  @click="addFriend(selectedUser)"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
            </v-autocomplete>
          </div>
          <div v-else-if="addFriendsTab === 'last played with'">
            <ul class="py-1 px-3 mt-2 last-played-with">
              <li
                v-if="!lastPlayedWith.length"
                class="text-center black--text font-size-20 font-weight-superbold py-3 empty"
              >
                Empty
              </li>
              <li
                v-for="(child, index) in lastPlayedWith"
                :key="index"
                class="d-flex align-center px-4 py-1"
              >
                <v-avatar class="mr-2 mr-sm-5" size="40">
                  <img
                    v-reload-on-fail
                    alt="John"
                    src="/images/icons/Profile_Pic.png"
                  />
                  <span
                    :class="child.online ? 'online' : 'offline'"
                    class="status"
                  />
                </v-avatar>
                <span
                  class="font-weight-semi text-subtitle-1 text-sm-h5 black--text"
                >
                  {{ child.displayName || child.name }}
                </span>

                <v-spacer />

                <div class="button-containers">
                  <v-btn
                    class="button-container green-light mr-2 cursor-pointer"
                    height="24"
                    icon
                    width="24"
                    @click="addFriend(child.displayName)"
                  >
                    <v-icon class="mr-0" color="white" size="16">
                      mdi-plus
                    </v-icon>
                  </v-btn>
                </div>
              </li>
            </ul>
          </div>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col
      :class="{
        active: activeContainer === 'friendList',
      }"
      class="friend-container left middle d-flex justify-center px-0 pt-0"
      cols="4"
      @mouseover="setActiveContainer('friendList')"
    >
      <v-card
        class="card-background-light ma-0 pa-0"
        elevation="10"
        height="100%"
        max-width="400"
        min-height="400"
        width="100%"
      >
        <v-card-title class="d-fle align-center justify-space-between">
          <friends-menu-icon width="50px" />

          <span class="title--text"> FRIEND LIST </span>
        </v-card-title>

        <v-divider class="mt-1" />
        <v-divider class="mb-1" />

        <v-card-text class="pb-0 px-3">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div class="d-flex align-center w-full pr-2">
                  ONLINE
                  <v-spacer />
                  <v-icon color="#000000"> mdi-account</v-icon>
                  {{ userFriendsOnline.length }}
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ul class="pr-0 pr-sm-4 mt-4">
                  <li
                    v-if="!userFriendsOnline.length"
                    class="text-center white--text font-size-20 font-weight-superbold py-3 empty"
                  >
                    Empty
                  </li>
                  <li
                    v-for="(child, index) in userFriendsOnline"
                    :key="index"
                    class="d-flex align-center px-3 tiny"
                  >
                    <div class="py-1">
                      <v-avatar class="mr-2 mr-sm-5" size="28">
                        <img
                          v-reload-on-fail
                          alt="John"
                          src="/images/icons/Profile_Pic.png"
                        />
                        <span class="status online" />
                      </v-avatar>
                    </div>
                    <span
                      class="text-subtitle-2 text-sm-subtitle-1 text-md-h6 bold white--text"
                    >
                      {{ child.displayName }}
                    </span>

                    <v-spacer />

                    <div :class="index" class="d-flex">
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <div
                            class="button-container mr-2"
                            v-bind="attrs"
                            @click="blockUser(child)"
                            v-on="on"
                          >
                            <v-icon class="mr-0" color="white" size="16">
                              mdi-block-helper
                            </v-icon>
                          </div>
                        </template>
                        <span>Block User</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <div
                            class="button-container"
                            v-bind="attrs"
                            @click="removeFriend(child)"
                            v-on="on"
                          >
                            <v-icon class="mr-0" color="white" size="16">
                              mdi-minus
                            </v-icon>
                          </div>
                        </template>
                        <span>Remove Friend</span>
                      </v-tooltip>
                    </div>
                  </li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header>
                <div class="d-flex align-center w-full pr-2">
                  OFFLINE
                  <v-spacer />
                  <v-icon color="#000000"> mdi-account</v-icon>
                  {{ userFriendsOffline.length }}
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ul class="pr-0 pr-sm-4 mt-4">
                  <li
                    v-if="!userFriendsOffline.length"
                    class="text-center white--text font-size-20 font-weight-superbold py-3 empty"
                  >
                    Empty
                  </li>
                  <li
                    v-for="(child, index) in userFriendsOffline"
                    :key="index"
                    class="d-flex align-center px-3 py-1 tiny"
                  >
                    <div class="py-1">
                      <v-avatar class="mr-2 mr-sm-5" size="28">
                        <img
                          v-reload-on-fail
                          alt="John"
                          src="/images/icons/Profile_Pic.png"
                        />
                        <span class="status offline" />
                      </v-avatar>
                    </div>
                    <span
                      class="text-subtitle-2 text-sm-subtitle-1 text-md-h6 bold white--text"
                    >
                      {{ child.displayName }}
                    </span>

                    <v-spacer />

                    <div :class="index" class="d-flex">
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <div
                            class="button-container mr-2"
                            v-bind="attrs"
                            @click="blockUser(child)"
                            v-on="on"
                          >
                            <v-icon class="mr-0" color="white" size="16">
                              mdi-block-helper
                            </v-icon>
                          </div>
                        </template>
                        <span>Block User</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <div
                            class="button-container"
                            v-bind="attrs"
                            @click="removeFriend(child)"
                            v-on="on"
                          >
                            <v-icon class="mr-0" color="white" size="16">
                              mdi-minus
                            </v-icon>
                          </div>
                        </template>
                        <span>Remove Friend</span>
                      </v-tooltip>
                    </div>
                  </li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div class="d-flex align-center w-full pr-2">
                  BLOCKED
                  <v-spacer />
                  <v-icon color="#000000"> mdi-account</v-icon>
                  {{ userFriendsBlocked.length }}
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ul class="pr-0 pr-sm-4 mt-4">
                  <li
                    v-if="!userFriendsBlocked.length"
                    class="text-center white--text font-size-20 font-weight-superbold py-3 empty"
                  >
                    Empty
                  </li>
                  <li
                    v-for="(child, index) in userFriendsBlocked"
                    :key="index"
                    class="d-flex align-center px-4 py-1 tiny"
                  >
                    <div class="py-1">
                      <v-avatar class="mr-2 mr-sm-5" size="28">
                        <img
                          v-reload-on-fail
                          alt="John"
                          src="/images/icons/Profile_Pic.png"
                        />
                        <span class="status online" />
                      </v-avatar>
                    </div>
                    <span
                      class="text-subtitle-2 text-sm-subtitle-1 text-md-h6 bold white--text"
                    >
                      {{ child.displayName }}
                    </span>

                    <v-spacer />

                    <div :class="index" class="d-flex blocked">
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <div
                            class="button-container mr-2"
                            v-bind="attrs"
                            @click="unblockUser(child)"
                            v-on="on"
                          >
                            <v-icon class="mr-0" color="white" size="16">
                              mdi-block-helper
                            </v-icon>
                          </div>
                        </template>
                        <span>Unblock User</span>
                      </v-tooltip>
                    </div>
                  </li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col
      :class="{
        active: activeContainer === 'friendRequest',
      }"
      class="friend-container right px-0 pt-0 d-flex justify-center"
      cols="4"
      @mouseover="setActiveContainer('friendRequest')"
    >
      <v-card
        class="card-background-light ma-0 pa-0"
        elevation="10"
        height="100%"
        max-width="400"
        min-height="400"
        width="100%"
      >
        <v-card-title class="d-fle align-center justify-space-between">
          <friends-add-icon width="50px" />

          <span class="title--text"> FRIEND REQUEST </span>
        </v-card-title>

        <v-divider class="mt-1" />
        <v-divider class="mb-1" />

        <v-card-text class="px-0">
          <div class="d-flex px-0 px-sm-3">
            <v-btn
              v-for="key in friendRequestCategoriesTitles"
              :key="key"
              :class="{
                'main-lighter': tab === key,
              }"
              :ripple="false"
              class="friend-request-button font-weight-superbold mx-1"
              elevation="0"
              style="flex: 1"
              @click="setTab(key)"
            >
              <span class="text-subtitle-1 text-md-h6 bold position-relative">
                {{ key }}
              </span>
              <div
                v-if="key === 'received' && userFriendsReceived.length > 0"
                class="receivedNotify"
              >
                <img
                  v-reload-on-fail
                  alt=""
                  src="/images/svgs/received-sign.svg"
                  width="10"
                />
              </div>
            </v-btn>
          </div>

          <div class="mt-8">
            <div
              v-for="(item, key) in friendRequestCategories"
              :key="key"
              class="tabs"
            >
              <ul v-if="tab === key" :class="key" class="py-1 px-3">
                <li
                  v-if="!item.length"
                  class="text-center black--text font-size-20 font-weight-superbold py-3 empty"
                >
                  Empty
                </li>
                <li
                  v-for="(child, index) in item"
                  :key="index"
                  class="d-flex align-center px-3 py-1"
                >
                  <v-avatar class="mr-2 mr-sm-5" size="40">
                    <img
                      v-reload-on-fail
                      alt="John"
                      src="/images/icons/Profile_Pic.png"
                    />
                    <span
                      :class="child.online ? 'online' : 'offline'"
                      class="status"
                    />
                  </v-avatar>
                  <span
                    class="font-weight-semi text-subtitle-1 text-sm-h5 black--text"
                  >
                    {{ child.displayName || child.name }}
                  </span>

                  <v-spacer />

                  <div class="button-containers">
                    <div
                      class="button-container green-light mr-2"
                      @click="acceptFriendRequest(child)"
                    >
                      <v-icon class="mr-0" color="white" size="16">
                        mdi-check
                      </v-icon>
                    </div>
                    <div
                      class="button-container red darken-2"
                      @click="rejectFriendRequest(child)"
                    >
                      <v-icon class="mr-0" color="white" size="16">
                        mdi-minus
                      </v-icon>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<!-- GLOBAL STYLES -->
<style lang="scss">
.menu-container {
  border-bottom-left-radius: 20px !important;
  border-bottom-right-radius: 20px !important;
  box-shadow: none !important;
  padding: 0 10px !important;
  overflow: hidden !important;
}
</style>

<style lang="scss" scoped>
.last-played-with {
  li {
    border-radius: 10px;
    background: white;
    margin: 10px 0;

    .button-container {
      button {
        width: 24px;
        height: 24px;
        border-radius: 10px;
      }
    }
  }
}

.friends-list-container {
  @media screen and (max-width: 900px) {
    padding: 10px 20px;
    max-width: 100%;
  }
}

.add-friend-button {
  background: var(--v-green-base);
  border-radius: 8px;
  height: 24px;
  width: 24px;
}

.friend-request-button {
  .friend-add-text {
    white-space: pre-wrap;
    width: 150px;
  }

  &::after {
    border-top: 10px solid #f5f5f5;
  }
}

.friend-container.left,
.friend-container.right {
  position: relative;
  padding-top: 72px;
  padding-bottom: 72px;
  max-width: 50%;
  z-index: 1;

  &.active {
    .v-card {
      z-index: 1;
      background: var(--v-main-light-base) !important;
    }
  }

  .v-card {
    border-radius: 10px !important;

    .title--text {
      font-weight: 900;
      color: white;
      font-size: 34px;
      margin-right: 24px;
    }
  }

  ul {
    min-height: 300px;
    overflow: auto;

    &::-webkit-scrollbar,
    &::-webkit-scrollbar-track {
      border-radius: 10px !important;
    }

    li {
      transition: transform 0.3s ease, background 0.3s ease;

      .v-avatar {
        overflow: visible;

        .status {
          position: absolute;
          bottom: -2px;
          right: -2px;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: var(--v-error-base);
          border: 2px solid var(--v-main-light-base);

          &.online {
            background: var(--v-green-light-base);
          }

          &.blocked {
            background: white;
          }
        }
      }

      &:not(.empty):not(.tiny):hover {
        background: var(--v-main-crisp-base) !important;
        transform: translateX(-20px);

        span {
          color: white !important;
        }
      }
    }
  }
}

.friend-container.left {
  padding-right: 90px;
  position: relative;

  .v-expansion-panels::v-deep {
    .v-expansion-panel {
      margin-top: 0 !important;
      margin-bottom: 16px;

      &:not(.v-expansion-panel-header--active) {
        border-radius: 10px !important;
      }

      .v-expansion-panel-header {
        font-weight: 900;
        text-transform: uppercase;
        font-size: 20px;
        min-height: 36px;
        height: 36px;
        padding: 4px 10px 6px;

        &.v-expansion-panel-header--active {
          position: relative;

          &::after {
            content: "";
            position: absolute;
            bottom: 4px;
            width: calc(100% - 24px);
            height: 1px;
            background: var(--v-main-light-base);
          }
        }

        .v-icon {
          color: #000000 !important;
        }
      }

      .v-expansion-panel-content__wrap {
        ul {
          list-style: none;
          padding-left: 0;

          li {
            background: var(--v-main-light-base);
            border-radius: 10px;

            &:not(:last-child) {
              margin-bottom: 10px;
            }

            .blocked {
              .button-container {
                background: transparent;

                i {
                  color: var(--v-green-light-base) !important;
                }
              }
            }

            .button-container {
              display: flex;
              align-items: center;
              justify-content: center;
              background: #da0000;
              width: 22px;
              height: 22px;
              border-radius: 4px;
              cursor: pointer;
            }
          }
        }
      }
    }
  }

  &::after {
    content: "";
    position: absolute;
    background-image: url("/images/backgrounds/Divider_Friends.png");
    background-repeat: no-repeat;
    background-size: 100% 100%;
    height: 100%;
    width: 50px;
    right: -25px;
    top: 0;

    @media screen and (max-width: 900px) {
      content: unset;
    }
  }

  ul {
    max-height: 390px;

    /* scrollbar */
    &::-webkit-scrollbar {
      background: #291d30 !important;
      width: 6px;
    }

    /* Track */
    &::-webkit-scrollbar-track {
      background: #8c8c8c !important;
      width: 6px;
    }

    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: #000000 !important;
    }

    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
      background: #383838 !important;
    }
  }
}

.friend-container.right {
  ul {
    //min-height: 44vh;
    max-height: 42vh;
  }
}

.friend-container {
  @media screen and (max-width: 1376px) {
    flex-basis: 50%;
  }
}

.divider {
  position: relative;
  height: 100%;
  vertical-align: text-bottom;
  margin: 0 -1px;

  .inside {
    position: absolute;
    background-image: url("/images/backgrounds/Divider_Friends.png");
    background-repeat: no-repeat;
    background-size: 100% 100%;
    height: 100%;
    width: 50px;
    right: 0;
  }
}

.friend-request-button {
  position: relative;

  &.main-lighter::after {
    content: "";
    position: absolute;
    left: 42%;
    top: 100%;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid #f5f5f5;
    clear: both;
  }

  &.main-lighter {
    background: var(--v-main-lighter-base) !important;

    span {
      color: white !important;
    }

    &::after {
      border-top: 10px solid var(--v-main-lighter-base) !important;
    }
  }

  span {
    color: black;
  }

  .receivedNotify {
    position: absolute;
    top: -22px;
    right: -8px;
    font-size: 26px;
    font-weight: 800;
    transform: rotate(5deg);
  }
}

.friend-container.right {
  padding-left: 90px;

  .tabs {
    ul {
      list-style: none;
      padding-left: 0;

      &.received {
        li {
          .button-containers {
            display: flex;
          }
        }
      }

      li {
        background: #ffffff;
        border-radius: 10px;

        &:not(:last-child) {
          margin-bottom: 10px;
        }

        .status {
          border-color: white;
        }

        .button-containers {
          display: none;

          .button-container {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 22px;
            height: 22px;
            border-radius: 4px;
            cursor: pointer;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 1440px) {
  .friend-container.left,
  .friend-container.right {
    padding-top: 58px;
    padding-bottom: 58px;
  }
}

@media screen and (max-width: 1100px) {
  .friend-container.left,
  .friend-container.right {
    svg {
      width: 40px;
    }

    .title--text {
      font-size: 28px !important;
    }
  }
  .friend-container.left {
    padding-right: 50px;
    padding-left: 20px;
  }
  .friend-container.right {
    padding-left: 50px;
    padding-right: 20px;
  }

  .friend-request-button {
    font-size: 16px !important;
  }
}

@media screen and (max-width: 900px) {
  .friend-container.left,
  .friend-container.right {
    padding: 20px 40px;
    justify-content: center;
    flex: 0 0 100%;
    max-width: 100%;

    .v-card {
      max-width: initial !important;
    }
  }

  .friend-container.left::v-deep,
  .friend-container.right::v-deep {
    .title--text {
      font-size: 20px !important;
    }

    .v-expansion-panel-content__wrap {
      padding: 0 6px 8px;
    }
  }

  .friend-container.left {
    ul {
      min-height: 150px;
      max-height: 230px;
    }
  }

  .friend-container.right {
    ul {
      min-height: 240px;
      max-height: 240px;
    }
  }

  .divider {
    &.desktop {
      display: none;
    }
  }

  .divider-m {
    position: relative;
    flex: 0 0 100%;
    max-width: 90%;
    left: 5%;
    height: 50px;
    border-radius: 10px;

    .inside {
      position: absolute;
      left: -7%;
      top: 0;
      width: 115%;
      height: 50px;
      background-image: url("/images/figures/Divider.png");
      background-repeat: no-repeat;
      background-size: 100% 100%;
      overflow: hidden;
    }
  }
}

@media screen and (max-width: 500px) {
  .friend-container.left,
  .friend-container.right {
    padding: 20px;
  }

  .friend-add-text {
    width: 120px !important;
  }

  .receivedNotify {
    top: -28px !important;
    right: 0 !important;
  }
}
</style>
